import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PhoneCallLink } from "@/components/site/PhoneCallLink";

type NextStepProps = {
  heading: string;
  body: string;
  primaryLabel: string;
  primaryTo: string;
  secondaryLabel?: string;
  secondaryTo?: string;
  primaryNote?: React.ReactNode;
  secondaryNote?: React.ReactNode;
};

function ButtonLink({
  to,
  className,
  label,
  children,
}: {
  to: string;
  className: string;
  label: string;
  children: React.ReactNode;
}) {
  if (to.startsWith("http")) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-analytics-cta={label}
        data-analytics-location="next_step"
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      to={to}
      className={className}
      data-analytics-cta={label}
      data-analytics-location="next_step"
    >
      {children}
    </Link>
  );
}

/**
 * The one closing block used at the end of every page. One primary action,
 * at most one secondary action, always on the dark surface so it reads as
 * the end of the page.
 */
export function NextStep({
  heading,
  body,
  primaryLabel,
  primaryTo,
  secondaryLabel,
  secondaryTo,
  primaryNote,
  secondaryNote,
}: NextStepProps) {
  return (
    <section className="bg-midnight-deep py-20 text-frost lg:py-24">
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <h2 className="font-serif text-3xl sm:text-4xl">{heading}</h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-frost/85">{body}</p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <div className="contents">
            <ButtonLink
              to={primaryTo}
              label={primaryLabel}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-frost px-7 text-sm font-semibold text-midnight-deep transition hover:bg-white"
            >
              {primaryLabel}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </ButtonLink>

          </div>
          {secondaryLabel && secondaryTo ? (
            <div className="contents">
              <ButtonLink
                to={secondaryTo}
                label={secondaryLabel}
                className="inline-flex min-h-12 items-center justify-center rounded-full border border-frost/40 px-7 text-sm font-semibold text-frost transition hover:border-frost hover:bg-white/10"
              >
                {secondaryLabel}
              </ButtonLink>

            </div>
          ) : null}
        </div>
        <div className="mt-3 flex flex-col items-center gap-2">
          {primaryNote}
          {secondaryNote}
          {primaryTo.includes("discovery-call-1-clone") ? <PhoneCallLink placement={new URL(primaryTo).searchParams.get("utm_content") ?? "next_step"} className="text-sm font-medium text-frost/70 hover:text-frost" /> : null}
          {secondaryTo?.includes("discovery-call-1-clone") ? <PhoneCallLink placement={new URL(secondaryTo).searchParams.get("utm_content") ?? "next_step"} className="text-sm font-medium text-frost/70 hover:text-frost" /> : null}
        </div>
      </div>
    </section>
  );
}
